<script setup>
const props = defineProps({
  company: {
    type: Object,
    required: true,
  },
  canEdit: {
    type: Boolean,
    required: false,
    default: false,
  },
})

const slideOverStore = useSlideOverStore()

const descriptionExpanded = ref(false)

const descriptionFirstPart = computed(() => {
  const description = props.company.about.description
  return description?.split('\n')[0] || ''
})

const descriptionRest = computed(() => {
  const description = props.company.about.description
  return description?.split('\n')?.slice(1)?.join('\n')?.trim() || ''
})
</script>

<template>
  <div class="flex flex-col bg-white px-6 py-8 shadow-sm lg:p-10">
    <div class="flex items-center justify-between">
      <h2 class="font-heading text-4xl font-black uppercase text-secondary">
        Over ons
      </h2>
      <Button
        v-if="canEdit"
        theme="primary"
        text="bewerken"
        @click="slideOverStore.openSlideover('CompaniesTags')"
      />
    </div>
    <TagsByGroup :tags="company?.tags" class="mt-6" />
    <h3 class="mt-6 text-2xl font-semibold">{{ company.name }}</h3>
    <p class="mt-6 text-lg">
      {{ descriptionFirstPart }}
    </p>
    <HeightTransition>
      <div v-if="descriptionRest && descriptionExpanded">
        <p class="whitespace-pre-line text-lg mt-4">
          {{ descriptionRest }}
        </p>
      </div>
    </HeightTransition>

    <div
      class="mt-4 flex justify-between"
      :class="{ 'flex-row-reverse': canEdit, 'flex-row': !canEdit }"
    >
      <Button
        v-if="canEdit"
        theme="primary"
        text="bewerken"
        class="h-fit w-fit"
        @click="slideOverStore.openSlideover('CompaniesPersonalia')"
      />

      <button
        v-if="descriptionRest && !descriptionExpanded"
        class="w-fit text-lg text-secondary underline"
        @click="descriptionExpanded = true"
      >
        Lees meer
      </button>
    </div>

    <div class="relative mt-10 aspect-video w-full">
      <div class="flex justify-between">
        <h2
          class="mb-4 font-heading text-4xl font-black uppercase text-secondary"
        >
          Kijkje in ons bedrijf
        </h2>
        <button
          v-if="canEdit"
          class="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-secondary transition-all duration-100 hover:bg-secondary hover:text-primary"
          @click="slideOverStore.openSlideover('CompaniesMedia')"
        >
          <IconEdit class="h-4 w-4" />
        </button>
      </div>
      <SliderWidget
        :items="company?.media || []"
        theme="minimal"
        class="aspect-video w-full"
      />
    </div>

    <div class="mt-10 flex items-center justify-between">
      <h2 class="font-heading text-4xl font-black uppercase text-secondary">
        Wat maakt werken bij ons zo leuk
      </h2>
      <Button
        v-if="canEdit"
        theme="primary"
        text="bewerken"
        class="h-fit w-fit"
        @click="slideOverStore.openSlideover('CompaniesWhyWorkWithUs')"
      />
    </div>

    <div class="mt-6 flex flex-col gap-3">
      <ExpandableItem
        v-for="(item, index) in company?.about?.whyWorkWithUs"
        :key="index"
        theme="block-grey"
      >
        <template #title>{{ item.title }}</template>
        <template #content
          ><p>{{ item.text }}</p></template
        >
      </ExpandableItem>
    </div>
  </div>
</template>
